"use client";

import React, { useState } from "react";
import { SimulacionesService } from "../../../services/admin/simulaciones/simulaciones.service";

interface ImportarJSONModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ImportarJSONModal: React.FC<ImportarJSONModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [jsonText, setJsonText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleImport = async () => {
    if (!jsonText.trim()) {
      setError("Por favor, pega el JSON con las preguntas");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Parsear el JSON
      const data = JSON.parse(jsonText);

      if (!data.preguntas || !Array.isArray(data.preguntas)) {
        throw new Error("El JSON debe contener un array 'preguntas'");
      }

      // Importar las preguntas
      const result = await SimulacionesService.importarPreguntasPAESDesdeJSON(
        data.preguntas
      );

      setSuccess(
        `✅ Importación exitosa: ${result.importadas} de ${result.total} preguntas importadas`
      );

      if (result.errores.length > 0) {
        setError(
          `⚠️ ${result.errores.length} errores: ${result.errores
            .map((e) => `Pregunta ${e.numero}: ${e.error}`)
            .join(", ")}`
        );
      }

      // Limpiar el formulario después de 3 segundos
      setTimeout(() => {
        setJsonText("");
        setSuccess("");
        setError("");
        onSuccess();
        onClose();
      }, 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? `Error al procesar JSON: ${err.message}`
          : "Error desconocido al procesar el JSON"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasteExample = () => {
    const exampleJSON = `{
   "preguntas": [
     {
       "contenido": "¿Cuál es el resultado de 3-(-1)(-1-5)?",
       "respuesta": "B",
       "explicacion": "Resolviendo paso a paso: 3-(-1)(-1-5) = 3-(-1)(-6) = 3-6 = -3. La respuesta correcta según el clavijero oficial es B) -3.",
       "anio": 2024,
       "periodo": "Regular",
       "numero": 1,
       "tipo": "OPCION_MULTIPLE",
       "asignaturaId": 1,
       "dificultad": 3,
       "tiempoEstimado": 60,
       "tags": ["aritmetica", "operaciones_basicas"],
       "fuente": "PAES 2024 - Forma 113",
       "alternativas": {
         "A": "-1",
         "B": "-3",
         "C": "-12",
         "D": "-24"
       },
       "tipoSimulacion": "M1"
     }
   ]
 }`;
    setJsonText(exampleJSON);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            Importar Preguntas PAES desde JSON
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              JSON de Preguntas PAES
            </label>
            <button
              onClick={handlePasteExample}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Pegar ejemplo
            </button>
          </div>
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder="Pega aquí el JSON con las preguntas PAES..."
            className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700 text-sm">{success}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleImport}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Importando..." : "Importar Preguntas"}
          </button>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">
            Formato requerido:
          </h3>
          <div className="text-sm text-gray-600 space-y-1">
            <p>• El JSON debe contener un array "preguntas"</p>
            <p>
              • Cada pregunta debe tener: contenido, respuesta, explicacion,
              anio, periodo, numero, tipo, asignaturaId
            </p>
            <p>• Periodos válidos: "Regular" o "Invierno"</p>
            <p>• Tipos válidos: "OPCION_MULTIPLE", "VERDADERO_FALSO", etc.</p>
            <p>• asignaturaId: 1 para Matemáticas</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportarJSONModal;
